<template>
  <div class="modal fade show my-2" aria-labelledby="makePaymentTitle" style="display: block; background: #1c1d1c33">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="row">
          <div class="col-12">
            <div class="d-flex justify-content-between px-1 my-1 align-item-center">
              <h4 class="mb-0">{{ modalTitle }}</h4>
              <button
                @click="onClickClose"
                type="button"
                class="btn-close"
                aria-label="Close"
              ></button>
            </div>
          </div>
        </div>

        <hr class="mt-0 mb-0">

        <div class="pb-2">
          <div class="row mx-2 mt-1">
            <div class="col-12">
              <label class="form-label">Ledger Date <span class="required">*</span></label>
              <input type="date" class="form-control" v-model="formData.ledger_date">
            </div>

            <template v-if="hasCheque">
              <div class="col-12 mt-1">
                <label class="form-label">Note <span class="required">*</span></label>
                <input type="text" class="form-control" v-model="formData.note">
              </div>

              <div class="col-12 mt-1">
                <label class="form-label">Bank Statement Date <span class="required">*</span></label>
                <input type="date" class="form-control" v-model="formData.bank_statement_date">
              </div>

              <div v-if="! forceClear" class="col-12 mt-2">
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="markApprovedCheck"
                    v-model="formData.mark_approved"
                  >
                  <label class="form-check-label" for="markApprovedCheck">
                    Mark as Approved (Cleared)
                  </label>
                </div>
                <small class="text-muted">
                  When checked: cheque marked cleared and ledger approved. Otherwise cheque marked paid.
                </small>
              </div>
            </template>

            <div class="col-12 mt-2">
              <label class="form-label fw-bold">Attachment</label>
              <BlobFileViewer :attachments="attachments" />
              <FileUpload
                :is-drag-active="true"
                button-text="Upload Documents"
                v-model="attachments"
              />
            </div>

            <div class="col-12 text-center mb-2 mt-2">
              <button
                :disabled="loading"
                @click="submit(false)"
                type="button"
                class="btn btn-primary me-1 waves-effect waves-float waves-light"
              >
                <div v-if="loading" class="spinner-border spinner-border-sm text-light" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                Save
              </button>
              <button
                :disabled="loading"
                @click="submit(true)"
                type="button"
                class="btn btn-success waves-effect waves-float waves-light"
              >
                <div v-if="loading" class="spinner-border spinner-border-sm text-light" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                Save & Print
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Loader v-if="loader" />
  </div>
</template>

<script setup>
import Loader from '@/components/atom/LoaderComponent'
import FileUpload from '@/components/atom/FileUpload'
import BlobFileViewer from '@/components/atom/BlobFileViewer'
import { inject, onMounted, ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import handlePayment from '@/services/modules/payment'
import handlePaymentRequisition from '@/services/modules/paymentRequisition'
import handleCompany from '@/services/modules/company'
import handleBarcode from '@/services/modules/barcode'
import paymentRequestVoucherPdf from '@/services/utils/paymentRequestVoucherPdf'

const props = defineProps({
  ledgerMasterId: { type: Number, required: true },
  forceClear: { type: Boolean, default: false },
})

const emit = defineEmits(['close', 'success'])

const $route = useRoute()
const showError = inject('showError')
const showSuccess = inject('showSuccess')

const { fetchSinglePayment } = handlePayment()
const { paymentRequisitionMakePayment, fetchPaymentRequisitionVoucherPdf } = handlePaymentRequisition()
const { fetchCompanyInfo } = handleCompany()
const { fetchBarcode } = handleBarcode()
const { exportToPDF } = paymentRequestVoucherPdf()

const companyId = computed(() => $route.params.companyId)

const loading = ref(false)
const loader = ref(false)
const hasCheque = ref(false)
const chequeTitle = ref('')
const attachments = ref([])

const formData = ref({
  note: '',
  bank_statement_date: '',
  ledger_date: '',
  mark_approved: false,
})

const modalTitle = computed(() => {
  const base = props.forceClear ? 'Clearance' : 'Make Payment'
  if (! chequeTitle.value) return base
  return `${base} - ${chequeTitle.value}`
})

const fetchLedgerInfo = async () => {
  loader.value = true
  try {
    const res = await fetchSinglePayment(props.ledgerMasterId)
    if (! res.status) {
      hasCheque.value = false
      return
    }
    if (Array.isArray(res.data.attachments)) {
      attachments.value = res.data.attachments
    }
    if (res.data.date) {
      formData.value.ledger_date = res.data.date
    }
    if (! res.data.cheque_register_id) {
      hasCheque.value = false
      return
    }
    hasCheque.value = true
    chequeTitle.value = res.data.cheque_number || ''
    if (res.data.cheque_note) {
      formData.value.note = res.data.cheque_note
    }
    if (res.data.cheque_bank_statement_date) {
      formData.value.bank_statement_date = res.data.cheque_bank_statement_date
    }
    if (props.forceClear) {
      formData.value.mark_approved = true
    }
  } finally {
    loader.value = false
  }
}

const validate = () => {
  if (! formData.value.ledger_date) {
    showError('Ledger date is required.')
    return false
  }
  if (! hasCheque.value) return true
  if (! formData.value.note) {
    showError('Note is required.')
    return false
  }
  if (! formData.value.bank_statement_date) {
    showError('Bank statement date is required.')
    return false
  }
  return true
}

const generateVoucherPdf = async () => {
  const companyQuery = `?company_id=${companyId.value}&with_approval=1`
  const [pdfRes, company] = await Promise.all([
    fetchPaymentRequisitionVoucherPdf(props.ledgerMasterId, companyQuery),
    fetchCompanyInfo(companyId.value),
  ])
  if (! pdfRes.status) {
    showError(pdfRes.message)
    return
  }
  const voucher = pdfRes.data
  const qrcodeData = JSON.stringify({
    ref_no: voucher.voucher_no,
    ref_date: voucher.date,
    created_date_time: new Date().toISOString(),
  })
  const barcodeRes = await fetchBarcode(`?code=${voucher.voucher_no}&qrcode=${qrcodeData}`)
  exportToPDF(company.data, voucher, barcodeRes.qrcode)
}

const submit = async (print = false) => {
  if (! validate()) return
  const confirmMsg = props.forceClear
    ? 'Are you sure you want to clear this cheque and approve the ledger?'
    : 'Are you sure you want to make this payment?'
  const confirmed = confirm(confirmMsg)
  if (! confirmed) return

  const data = new FormData()
  data.append('company_id', $route.params.companyId)
  data.append('ledger_date', formData.value.ledger_date)
  if (hasCheque.value) {
    data.append('note', formData.value.note)
    data.append('bank_statement_date', formData.value.bank_statement_date)
    const markApproved = props.forceClear ? 1 : (formData.value.mark_approved ? 1 : 0)
    data.append('mark_approved', markApproved)
  }
  const previousFileIds = []
  attachments.value.forEach((att, i) => {
    if (att && typeof att === 'object' && att.id) {
      previousFileIds.push(att.id)
      return
    }
    data.append(`attachments[${i}]`, att)
  })
  data.append('previous_file_ids', JSON.stringify(previousFileIds))

  loading.value = true
  try {
    const res = await paymentRequisitionMakePayment(props.ledgerMasterId, data)
    if (! res.status) {
      showError(res.message)
      return
    }
    showSuccess(res.message)
    if (print) await generateVoucherPdf()
    emit('success')
    emit('close')
  } finally {
    loading.value = false
  }
}

const onClickClose = () => emit('close')

onMounted(() => {
  formData.value.bank_statement_date = new Date().toISOString().split('T')[0]
  fetchLedgerInfo()
})
</script>
